import moment from "moment";
import { getClearPeriodDateRange } from "./eventsWorkspaceClear.utils";
import { getSchedulerTemplateViewType } from "./eventsWorkspaceSaveTemplate.utils";

const EVENT_SHIFT_VIEW = {
  DAILY: "3",
  MONTHLY: "2",
  WEEKLY: "4",
};

/** Default page size for the Add from Saved Template list. */
export const APPLY_TEMPLATE_PAGE_SIZE = 50;

/** Debounce delay (ms) before NameSearch hits GetAllTemplate. */
export const APPLY_TEMPLATE_SEARCH_DEBOUNCE_MS = 300;

/**
 * Formats the target period label shown in the apply-template confirmation text.
 * Weekly/monthly use "MMM D - MMM D"; daily uses "MMM D, YYYY".
 * @param {string} calendarViewId
 * @param {Date|string} date
 * @returns {string}
 */
export function getApplyTemplatePeriodLabel(calendarViewId, date) {
  const anchor = moment(date);
  if (!anchor.isValid()) return "";

  if (calendarViewId === EVENT_SHIFT_VIEW.DAILY) {
    return anchor.format("MMM D, YYYY");
  }

  if (calendarViewId === EVENT_SHIFT_VIEW.MONTHLY) {
    const start = anchor.clone().startOf("month");
    const end = anchor.clone().endOf("month");
    return `${start.format("MMM D")} - ${end.format("MMM D")}`;
  }

  const start = anchor.clone().startOf("week");
  const end = anchor.clone().endOf("week");
  return `${start.format("MMM D")} - ${end.format("MMM D")}`;
}

/**
 * Modal copy details for Add from Saved Template (title + day/week/month dates).
 * @param {string} calendarViewId
 * @param {Date|string} date
 * @returns {{ titleKey: string, introKey: string, periodValue: string }}
 */
export function getApplyTemplatePeriodDetails(calendarViewId, date) {
  const introKeyByView = {
    [EVENT_SHIFT_VIEW.DAILY]: "scheduler.applyTemplateIntroDay",
    [EVENT_SHIFT_VIEW.WEEKLY]: "scheduler.applyTemplateIntroWeek",
    [EVENT_SHIFT_VIEW.MONTHLY]: "scheduler.applyTemplateIntroMonth",
  };

  return {
    titleKey: "scheduler.addFromSavedTemplate",
    introKey:
      introKeyByView[calendarViewId] || "scheduler.applyTemplateIntroWeek",
    periodValue: getApplyTemplatePeriodLabel(calendarViewId, date),
  };
}

/**
 * Normalizes GetAllTemplate response into `{ id, name }[]`.
 * Accepts common list envelopes (array, data[], items[], templates[]).
 * @param {unknown} response
 * @returns {Array<{ id: number, name: string }>}
 */
export function normalizeSchedulerTemplateList(response) {
  const root = response?.data ?? response;
  const list = Array.isArray(root)
    ? root
    : Array.isArray(root?.items)
      ? root.items
      : Array.isArray(root?.templates)
        ? root.templates
        : Array.isArray(root?.data)
          ? root.data
          : [];

  return list
    .map((item) => {
      const id = item?.id ?? item?.templateId ?? item?.TemplateId;
      const name = item?.name ?? item?.templateName ?? item?.Name ?? "";
      if (id == null || !String(name).trim()) return null;
      return { id: Number(id), name: String(name).trim() };
    })
    .filter(Boolean);
}

/**
 * Builds SchedulerTemplate/Apply POST body for the currently selected period.
 * @param {{
 *   ownerId: number,
 *   templateId: number,
 *   calendarViewId: string,
 *   currentDate: Date|string,
 * }} options
 * @returns {{ OwnerId: number, TemplateId: number, TargetStartDate: string }}
 */
export function buildApplySchedulerTemplatePayload({
  ownerId,
  templateId,
  calendarViewId,
  currentDate,
}) {
  const { startDate } = getClearPeriodDateRange(calendarViewId, currentDate);
  return {
    OwnerId: ownerId,
    TemplateId: templateId,
    TargetStartDate: startDate,
  };
}

/**
 * ViewType for GetAllTemplate matching the active calendar view.
 * @param {string} calendarViewId
 * @returns {number}
 */
export function getApplyTemplateViewType(calendarViewId) {
  return getSchedulerTemplateViewType(calendarViewId);
}
